<script setup>
// =============================================================================
// FangateAddAccountModal.vue
// =============================================================================
// 6-step in-place "Add account" wizard. Matches the prototype at
// fangate-crm-prototype.html lines 4023+ — Platform → Type → Token → Name →
// Agents → Fangate link.
//
// Telegram type matrix (v1, Channel removed):
//   • Bot-Account      — standalone @BotFather bot. Fans DM the bot directly.
//   • Personal-Account — SAME bot_token, but the creator additionally connects
//                        the bot's @username in Telegram → Settings →
//                        Telegram Business → Chatbots (requires Premium on
//                        the creator's own account). Inbound messages then
//                        arrive as `business_message` updates; Chatwoot
//                        already handles them via
//                        Telegram::IncomingMessageService#transform_business_message!.
//                        Personal vs Bot is detected at runtime from the
//                        conversation's business_connection_id flag — no
//                        inbox-level marker stored, so this modal sends
//                        only bot_token.
//
// Future (Path A / Pre-Release task #123): MTProto User API for Premium-free
// flow. NOT this modal — that needs a separate auth wizard (phone + code
// + 2FA + warmup) and a Python sidecar. api_id MUST NEVER reach this file.
//
// Modal contract:
//   • Teleported to body so it can't be clipped by side-panel ancestors.
//   • Backdrop click does NOT dismiss (per request) — only the × button.
//   • Dirty tracking — close triggers a "Discard changes?" confirm if any
//     non-default field was touched.
// =============================================================================

/* global axios */
import { ref, reactive, computed, watch, nextTick, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import FangateAPI from 'dashboard/api/fangate';

const props = defineProps({
  open:    { type: Boolean, default: false },
  agents:  { type: Array,   default: () => [] },
  // Existing Fangate-linked inboxes — drives the "existing vault" picker
  // in step 6 so the user can attach a new Telegram account to an already-
  // configured Fangate vault instead of pasting the token again.
  inboxes: { type: Array,   default: () => [] },
  // When set (an inbox object), the modal runs in RECONNECT mode: only the
  // Enduser connect-wizard shows (no platform/type/name/agents/fangate steps),
  // and on success the backend re-auths THAT inbox in place (keeps its chats).
  reconnectInbox: { type: Object, default: null },
});

const emit   = defineEmits(['update:open', 'created']);
const store  = useStore();
const isReconnect = computed(() => !!props.reconnectInbox);

// Only inboxes that ACTUALLY have a Fangate vault linked (a stored token) may be
// offered as an "existing vault" to reuse. Previously the picker listed EVERY
// inbox as a vault — phantom entries for accounts that were never linked.
// Labelled with the vault email so it's identifiable (e.g. "Account — info@…").
const fangateVaults = computed(() =>
  props.inboxes
    .filter(i => FangateAPI.isInboxConnected(i.id))
    .map(i => ({ id: i.id, name: i.name, email: FangateAPI.getEmailForInbox(i.id) }))
);

// ---- Wizard state ---------------------------------------------------------
const PLATFORM_OPTIONS = [
  { id: 'telegram', label: 'Telegram' },
  { id: 'whatsapp', label: 'WhatsApp', disabled: true },
];

// Channel type removed for v1 — preview-mode and we want a clean first launch.
// Re-add when channel_post handling is polished (post-launch roadmap).
const TG_TYPE_OPTIONS = [
  {
    id: 'bot',
    label: 'Bot-Account',
    intro: 'A standalone bot — your fans DM the bot directly under its @username. Quick to set up, no Premium needed.',
    steps: [
      {
        title: 'Create the bot in @BotFather',
        body: 'Open @BotFather in Telegram, send /newbot, pick a display name and a username ending in "bot". @BotFather gives you a bot_token — paste it into the field below.',
      },
    ],
  },
  {
    id: 'enduser',
    label: 'Enduser Account',
    subtitle: 'via Telephone Number — no Premium / Bot',
    // Enduser = MTProto user-account bridge. Distinct flow: no bot_token, an
    // interactive wizard (phone/SMS or QR) handles the login. See wizard*
    // state below. The intro/steps fields stay empty — the wizard renders
    // its own UI in place of the bot-token field.
    enduser: true,
    intro: '',
    steps: [],
  },
  {
    id: 'personal',
    label: 'Personal-Account',
    subtitle: 'Requires Telegram Premium',
    intro: 'Technically this is still a bot in the background — but to your fans it looks like YOU are writing. Your name, your profile picture, your account. The bot just routes the messages so Creator-Chat can answer them; replies appear to come from your personal account, not from a bot.',
    steps: [
      {
        title: 'Create the bot in @BotFather',
        body: 'Open @BotFather in Telegram, send /newbot, pick a display name and a username ending in "bot". @BotFather gives you a bot_token — paste it into the field below.',
      },
      {
        title: 'Enable SECRETARY MODE for the bot',
        body: 'Still in @BotFather: /mybots → pick the bot you just made → Bot Settings → Secretary Mode → turn it on. Without this you get the error "This bot does not yet support Telegram Business".',
      },
      {
        title: 'Add the bot\'s @username in your Telegram',
        body: 'Open Telegram → Settings → Telegram Business → Chatbots → paste the bot\'s @username and confirm. The menu only appears on Premium accounts. From now on, every DM to your personal account is also forwarded to the bot — and Creator-Chat can reply on your behalf.',
      },
      {
        title: 'Send one message from your phone',
        body: 'After connecting, send at least one message from your phone to a real Telegram contact (a person, not a bot). That\'s how Creator-Chat learns who owns this account, so incoming chats mirror into your own inbox. Until then messages still arrive — just only in the bot\'s inbox.',
      },
    ],
    premium: true,
  },
];

const initialState = () => ({
  platform:    'telegram',
  type:        'bot',
  token:       '',          // bot_token OR api_hash, depending on type
  name:        '',
  selectedAgentIds: [],
  fangateMode: '',          // '' = no link, '<inboxId>' = reuse existing, '__new__' = create new vault
  newVault: {
    email:     '',
    token:     '',
  },
});
const form = reactive(initialState());

// Step-1 platform-picker click. Reset Type to a valid default when leaving
// Telegram (WhatsApp doesn't use the type matrix).
const selectPlatform = id => {
  if (PLATFORM_OPTIONS.find(p => p.id === id)?.disabled) return;
  form.platform = id;
  if (id !== 'telegram') form.type = 'bot';
};
const selectType = id => {
  form.type = id;
  // Switching INTO enduser → arm the wizard. Switching OUT → cancel any
  // half-done auth so a stale auth_id can't survive a user changing their mind.
  if (id === 'enduser') resetWizard();
  else if (wizard.phase !== 'idle') cancelWizard();
};

// ---- Enduser (MTProto) onboarding wizard -----------------------------------
// Phases:
//   idle        → not started (gate; for non-enduser types)
//   risk        → disclosure + checkbox; gates everything else
//   method      → pick phone/SMS (default) vs QR
//   phone       → enter number
//   phone_code  → enter SMS code
//   password    → 2FA password (shared by phone + QR)
//   qr          → render QR + poll for scan
//   done        → backend created channel+inbox; expose inbox_id to submit()
// auth_id is the sidecar's per-attempt handle, passed through every call.
const wizard = reactive({
  phase:        'idle',
  loginMethod:  'qr',        // 'qr' (default, recommended) | 'device' (#35)
  acceptedRisk: false,
  authId:       '',
  phone:        '',
  code:         '',
  password:     '',
  qrUrl:        '',          // tg://login?token=... (raw)
  qrDataUrl:    '',          // rendered QR data URI for <img>
  qrExpiresAt:  '',
  busy:         false,
  error:        '',
  result:       null,        // { inbox_id, inbox_name, me } on success
  // own-api_id-first (#35): the agency's Telegram app credentials. credsChecked
  // gates the apicreds step; apiIdInput/apiHashInput back the gate form. apiId/
  // apiHash feed the in-browser GramJS device login.
  apiId:        '',
  apiHash:      '',
  device:       null,       // per-NUMBER device profile (#35), fetched per attempt
  country:      '',         // #3: registered country (ISO) → drives the proxy region
  credsPresent: false,
  credsChecked: false,
  apiIdInput:   '',
  apiHashInput: '',
  useDefaultCreds: false,   // fallback: adopt Creator-Chat's shared default api_id/api_hash
  defaultAvailable: false,  // server has a shared default configured (ENV)
});

// Country dropdown for onboarding (#3). The selected country drives the proxy
// region SERVER-side (ProxyRegionResolver is authoritative); this list + the
// region label are only for the UI hint. Broad-but-curated.
const countries = [
  { code: 'DE', name: 'Germany' }, { code: 'AT', name: 'Austria' }, { code: 'CH', name: 'Switzerland' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'IE', name: 'Ireland' }, { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' }, { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' }, { code: 'IT', name: 'Italy' }, { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' }, { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' }, { code: 'GR', name: 'Greece' },
  { code: 'RO', name: 'Romania' }, { code: 'HU', name: 'Hungary' }, { code: 'UA', name: 'Ukraine' },
  { code: 'TR', name: 'Turkey' }, { code: 'AE', name: 'United Arab Emirates' }, { code: 'SA', name: 'Saudi Arabia' },
  { code: 'IL', name: 'Israel' }, { code: 'IN', name: 'India' }, { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' }, { code: 'TH', name: 'Thailand' }, { code: 'VN', name: 'Vietnam' },
  { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' }, { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' }, { code: 'ZA', name: 'South Africa' }, { code: 'NG', name: 'Nigeria' },
  { code: 'EG', name: 'Egypt' },
  { code: 'US', name: 'United States' }, { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' }, { code: 'AR', name: 'Argentina' }, { code: 'CO', name: 'Colombia' },
  { code: 'CL', name: 'Chile' }, { code: 'PE', name: 'Peru' }, { code: 'VE', name: 'Venezuela' },
  { code: 'EC', name: 'Ecuador' }, { code: 'DO', name: 'Dominican Republic' },
];
const AMERICAS_CODES = new Set(['US', 'CA', 'MX', 'BR', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY',
  'GY', 'SR', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA', 'CU', 'DO', 'HT', 'JM', 'BS', 'BB', 'TT', 'PR']);
const proxyRegionLabel = code => (AMERICAS_CODES.has((code || '').toUpperCase()) ? 'United States' : 'Germany');
let qrPollTimer = null;
// The in-browser GramJS login instance (#35 device login). Kept OUT of the
// reactive wizard object — it holds a live MTProto client (sockets, big crypto
// objects) that must never be made reactive.
let deviceLogin = null;
const disconnectDevice = () => {
  if (!deviceLogin) return;
  const d = deviceLogin;
  deviceLogin = null;
  d.disconnect().catch(() => {});
};
const stopQrPoll = () => { if (qrPollTimer) { clearTimeout(qrPollTimer); qrPollTimer = null; } };

const accountId = computed(() => store.getters.getCurrentAccountId);
const authUrl = path => `/api/v1/accounts/${accountId.value}/telegram_user_auth/${path}`;

const resetWizard = () => {
  stopQrPoll();
  disconnectDevice();
  wizard.phase = 'risk';
  wizard.loginMethod = 'qr';
  wizard.acceptedRisk = false;
  wizard.authId = '';
  wizard.phone = '';
  wizard.code = '';
  wizard.password = '';
  wizard.qrUrl = '';
  wizard.qrDataUrl = '';
  wizard.qrExpiresAt = '';
  wizard.busy = false;
  wizard.error = '';
  wizard.result = null;
  wizard.apiIdInput = '';
  wizard.apiHashInput = '';
  wizard.useDefaultCreds = false;
  wizard.country = '';
  wizard.device = null;
  // apiId/apiHash/credsPresent are (re)loaded by ensureCreds(); reset credsChecked
  // so the gate re-checks on each open.
  wizard.credsChecked = false;
};

const cancelWizard = async () => {
  stopQrPoll();
  disconnectDevice();
  const id = wizard.authId;
  // Fire-and-forget: server will GC stale slots after 10 min anyway, but a
  // clean cancel disconnects the sidecar's auth client immediately.
  if (id) {
    try { await axios.post(authUrl('cancel'), { auth_id: id }); } catch (_) { /* noop */ }
  }
  wizard.phase = 'idle';
  wizard.authId = '';
};

// Reconnect mode: when the modal opens for an existing inbox, jump straight to
// the connect step (skip platform/type/risk) and arm the wizard at 'method'.
watch(() => props.open, async isOpen => {
  if (!isOpen || !props.reconnectInbox) return;
  form.platform = 'telegram';
  form.type = 'enduser';
  resetWizard();
  wizard.acceptedRisk = true;   // skip the risk disclosure — it's an existing account
  await ensureCreds();          // own-api_id-first: still need the agency creds set
  if (wizard.credsPresent) await proceedAfterCreds();  // reconnect → fetch device → method (keeps existing country/proxy)
  else                     wizard.phase = 'apicreds';
});

// Map sidecar error codes → English copy. Anything we don't recognise falls
// through to the raw `detail` so the agent at least sees something.
const friendlyError = data => {
  const code = data?.error || '';
  const detail = data?.detail || '';
  const map = {
    phone_invalid:    'That phone number doesn\'t look right. Use international format, e.g. +49…',
    phone_banned:     'This phone number is banned by Telegram. Use a different one.',
    voip_number:      detail || 'This looks like a VoIP / virtual number. Telegram bans these within minutes — please use a real mobile SIM number.',
    code_invalid:     'That code is wrong. Check the most recent Telegram message and try again.',
    code_expired:     'That code expired. Restart and request a new one.',
    password_invalid: 'Wrong 2FA password.',
    unknown_auth_id:  'This onboarding attempt expired. Please restart.',
    // #35 verify-on-save: the api_id/api_hash was checked against Telegram and rejected.
    invalid_credentials: detail || 'Telegram rejected your api_id / api_hash. Copy both from the SAME app at my.telegram.org.',
    // #42: a new number must declare its country before the login can start.
    country_required: detail || 'Please select the country this number is registered in before continuing.',
    flood_wait:       `Too many attempts. Telegram is rate-limiting us — wait ${data?.seconds ?? '?'} seconds and try again.`,
    admin_required:   'Only administrators can connect an enduser account.',
    // Login worked on Telegram's side, but the server couldn't finish setup
    // (e.g. DB error). The same code can't be reused — restart from scratch.
    persist_failed:   detail || 'Login succeeded but inbox creation failed. Please restart the wizard.',
    sidecar_error:    detail || 'Could not reach the Telegram bridge. Please try again in a moment.',
  };
  return map[code] || detail || 'Something went wrong. Please try again.';
};

const wizardRequest = async (path, body = {}) => {
  wizard.busy = true;
  wizard.error = '';
  try {
    const resp = await axios.post(authUrl(path), body);
    return resp?.data || {};
  } catch (e) {
    wizard.error = friendlyError(e?.response?.data || {});
    throw e;
  } finally {
    wizard.busy = false;
  }
};

const acceptRisk = async () => {
  if (!wizard.acceptedRisk) return;
  await ensureCreds();
  // own-api_id-first (#35): no agency api_id yet → set it up first; else continue.
  if (wizard.credsPresent) await proceedAfterCreds();
  else                     wizard.phase = 'apicreds';
};

// After the api_id gate: a NEW number picks its country (#3, drives the proxy
// region); a RECONNECT keeps the existing number's country/proxy → straight to
// the login method. Either way fetch a fresh per-attempt device profile.
const proceedAfterCreds = async () => {
  if (isReconnect.value) {
    await fetchNewDevice();
    wizard.phase = 'method';
  } else {
    wizard.phase = 'country';
  }
};

// Fresh per-NUMBER device profile for THIS attempt (#35/#3) — used for the
// in-browser login and stored on the new channel (login + runtime stay consistent).
const fetchNewDevice = async () => {
  try {
    const { data } = await axios.get(authUrl('new_device'));
    wizard.device = data.device || null;
  } catch (_) {
    wizard.device = null;
  }
};

// Country step → method. Fetch the device here so it's ready for QR + device login.
const confirmCountry = async () => {
  if (!wizard.country) return;
  await fetchNewDevice();
  wizard.phase = 'method';
};

const startMethod = () => {
  // #42: a NEW number must have a country before any login starts (it drives the proxy
  // region). Reconnects keep the existing number's country, so they're exempt.
  if (!isReconnect.value && !wizard.country) {
    wizard.error = 'Please select the country this number is registered in first.';
    wizard.phase = 'country';
    return;
  }
  if (wizard.loginMethod === 'qr') kickoffQr();
  else                             wizard.phase = 'phone';
};

// ---- own-api_id-first credential gate (#35) --------------------------------
// Fetch the account's stored Telegram app credentials once per open. The
// api_hash is the agency's own secret (admin-only endpoint) and is needed by the
// in-browser GramJS client.
const ensureCreds = async () => {
  if (wizard.credsChecked) return;
  try {
    const { data } = await axios.get(authUrl('credentials'));
    wizard.apiId = data.api_id || '';
    wizard.apiHash = data.api_hash || '';
    wizard.credsPresent = !!data.present;
    wizard.defaultAvailable = !!data.default_available;
  } catch (_) {
    wizard.credsPresent = false;
  } finally {
    wizard.credsChecked = true;
  }
};

// Fallback (#35): tick "use default" to adopt Creator-Chat's SHARED default creds.
// The values live in SERVER ENV — the client only sends a use_default flag; the
// backend verifies + stores them (kept out of the bundle/repo per #130). Using one's
// OWN credentials stays the lower-risk choice (no shared-id reputation clustering).
const toggleDefaultCreds = () => {
  wizard.error = '';
  // Manual inputs are hidden in default mode; clear them so nothing stale rides along.
  if (wizard.useDefaultCreds) {
    wizard.apiIdInput = '';
    wizard.apiHashInput = '';
  }
};

const saveApiCreds = async () => {
  const useDefault = wizard.useDefaultCreds;
  const apiId = wizard.apiIdInput.trim();
  const apiHash = wizard.apiHashInput.trim();
  if (!useDefault && (!apiId || !apiHash)) return;
  wizard.busy = true;
  wizard.error = '';
  try {
    // use_default → backend fills + verifies the shared default from ENV (client sends
    // no values); otherwise send the agency's own pair. Both go through verify-on-save.
    await axios.post(authUrl('credentials'), useDefault ? { use_default: true } : { api_id: apiId, api_hash: apiHash });
    wizard.credsPresent = true;
    if (useDefault) {
      // Re-load so the now-stored default creds populate wizard.apiId/apiHash — the
      // in-browser device login (GramJS) needs them; QR login uses them server-side.
      wizard.credsChecked = false;
      await ensureCreds();
    } else {
      wizard.apiId = apiId;
      wizard.apiHash = apiHash;
    }
    await proceedAfterCreds();
  } catch (e) {
    wizard.error = friendlyError(e?.response?.data || {});
  } finally {
    wizard.busy = false;
  }
};

// Jump back to the api-keys step to replace the stored credentials (#35). Handles
// a pre-verify bad pair that's already stored, or simply rotating the api_id later
// (there was no other way to change it). Clears the inputs so the user types fresh
// values; saveApiCreds re-verifies against Telegram + overwrites.
const reenterApiCreds = () => {
  wizard.apiIdInput = '';
  wizard.apiHashInput = '';
  wizard.useDefaultCreds = false;
  wizard.error = '';
  wizard.phase = 'apicreds';
};

// ---- Device login (#35): the WHOLE login runs in the customer's browser via
// GramJS (residential IP → no datacenter code suppression), under the agency's
// own api_id. We capture the resulting auth_key+dc and hand them to the bridge.
const friendlyDeviceError = err => {
  const msg = String((err && (err.errorMessage || err.message)) || '');
  if (msg.includes('PHONE_NUMBER_INVALID')) return 'That phone number doesn\'t look right. Use international format, e.g. +49…';
  if (msg.includes('PHONE_NUMBER_BANNED'))  return 'This phone number is banned by Telegram. Use a different one.';
  if (msg.includes('PHONE_CODE_INVALID'))   return 'That code is wrong. Check the latest message from "Telegram" and try again.';
  if (msg.includes('PHONE_CODE_EXPIRED'))   return 'That code expired. Restart and request a new one.';
  if (msg.includes('PASSWORD_HASH_INVALID')) return 'Wrong 2FA password.';
  const flood = msg.match(/FLOOD_WAIT_(\d+)/);
  if (flood) return `Too many attempts — Telegram is rate-limiting. Wait ${flood[1]}s and try again.`;
  if (msg.includes('API_ID') || msg.includes('API_HASH')) {
    return 'Telegram rejected your api_id / api_hash. Re-check them — they must match exactly from my.telegram.org.';
  }
  return msg || 'Login failed. Please restart and try again.';
};

// Surface either a server (bridge) error payload or a client-side GramJS error.
const setLoginError = e => {
  wizard.error = e?.response?.data ? friendlyError(e.response.data) : friendlyDeviceError(e);
};

const deviceStart = async () => {
  const phone = wizard.phone.trim();
  if (!phone) return;
  wizard.busy = true;
  wizard.error = '';
  try {
    const { TelegramDeviceLogin } = await import('../lib/telegramDeviceLogin');
    disconnectDevice();
    deviceLogin = new TelegramDeviceLogin({ apiId: wizard.apiId, apiHash: wizard.apiHash, device: wizard.device });
    await deviceLogin.connect();
    await deviceLogin.requestCode(phone);
    wizard.phase = 'phone_code';
  } catch (e) {
    setLoginError(e);
  } finally {
    wizard.busy = false;
  }
};

const deviceSubmitCode = async () => {
  const code = wizard.code.trim();
  if (!code || !deviceLogin) return;
  wizard.busy = true;
  wizard.error = '';
  try {
    const res = await deviceLogin.submitCode(code);
    if (res.passwordNeeded) { wizard.phase = 'password'; return; }
    await finalizeDevice();
  } catch (e) {
    setLoginError(e);
  } finally {
    wizard.busy = false;
  }
};

const deviceSubmitPassword = async () => {
  if (!wizard.password || !deviceLogin) return;
  wizard.busy = true;
  wizard.error = '';
  try {
    await deviceLogin.submitPassword(wizard.password);
    await finalizeDevice();
  } catch (e) {
    setLoginError(e);
  } finally {
    wizard.busy = false;
  }
};

// Export the credential from the browser client, disconnect it (so the same
// auth_key isn't live on two connections), then hand auth_key+dc to the server
// bridge, which imports them into a Telethon StringSession + creates/reconnects
// the inbox via the shared relay path.
const finalizeDevice = async () => {
  const cred = await deviceLogin.exportCredential();
  disconnectDevice();
  const { data } = await axios.post(authUrl('bridge'), {
    auth_key: cred.authKeyHex,
    dc_id: cred.dcId,
    inbox_name: form.name.trim() || undefined,
    reconnect_inbox_id: props.reconnectInbox?.id,
    country: wizard.country, device: wizard.device,
  });
  if (data.inbox_id) finishWizard(data);
  else throw new Error('The server could not finish setup. Please restart.');
};

const kickoffQr = async () => {
  wizard.phase = 'qr';
  try {
    const data = await wizardRequest('qr_start', { country: wizard.country, device: wizard.device, reconnect_inbox_id: props.reconnectInbox?.id });
    wizard.authId = data.auth_id || '';
    await applyQrToken(data.token_url, data.expires_at);
    pollQr();
  } catch (_) { /* shown via wizard.error */ }
};

const applyQrToken = async (tokenUrl, expiresAt) => {
  if (!tokenUrl || tokenUrl === wizard.qrUrl) return;
  wizard.qrUrl = tokenUrl;
  wizard.qrExpiresAt = expiresAt || '';
  try {
    wizard.qrDataUrl = await QRCode.toDataURL(tokenUrl, { margin: 1, width: 240 });
  } catch (_) { wizard.qrDataUrl = ''; }
};

const pollQr = () => {
  stopQrPoll();
  qrPollTimer = setTimeout(async () => {
    if (wizard.phase !== 'qr') return;
    try {
      const data = await wizardRequest('qr_poll', {
        auth_id: wizard.authId, inbox_name: form.name.trim() || undefined,
        reconnect_inbox_id: props.reconnectInbox?.id,
        country: wizard.country, device: wizard.device,
      });
      if (data.phase === 'password_needed') { stopQrPoll(); wizard.phase = 'password'; return; }
      if (data.inbox_id)                    { stopQrPoll(); finishWizard(data); return; }
      // Still waiting — refresh the QR if the sidecar rotated the token.
      if (data.token_url) await applyQrToken(data.token_url, data.expires_at);
      pollQr();
    } catch (_) {
      // Network blips: back off briefly, keep trying. User can hit Cancel.
      qrPollTimer = setTimeout(pollQr, 3000);
    }
  }, 2000);
};

const finishWizard = data => {
  stopQrPoll();
  wizard.result = data;
  wizard.phase = 'done';
  // Refresh the inbox list so the new inbox shows up in the sidebar
  // immediately (no full reload needed).
  store.dispatch('inboxes/get').catch(() => {});
};

onUnmounted(() => { stopQrPoll(); disconnectDevice(); });

// Pre-fill the Fangate email from an existing vault when one is picked, so the
// connected account's identity carries over (stays editable). Cleared for a
// brand-new vault or "don't link".
watch(() => form.fangateMode, mode => {
  if (mode && mode !== '__new__') {
    form.newVault.email = FangateAPI.getEmailForInbox(Number(mode)) || '';
  } else {
    form.newVault.email = '';
  }
});

const toggleAgent = id => {
  const i = form.selectedAgentIds.indexOf(id);
  if (i === -1) form.selectedAgentIds.push(id);
  else          form.selectedAgentIds.splice(i, 1);
};

// Active Telegram-type metadata — drives the intro + numbered steps under
// the Type pill grid.
const currentType = computed(() =>
  TG_TYPE_OPTIONS.find(t => t.id === form.type) || null
);

// Agent multi-select dropdown state. agentSummary renders the trigger label:
// "Select agents" when nothing's picked, the single name if exactly one is
// picked, and "N selected" otherwise — keeps the button compact.
// Admins are NOT listed — they always have access to every account.
const assignableAgents = computed(() =>
  (props.agents || []).filter(a => a.role !== 'administrator')
);
const agentMenuOpen = ref(false);
const closeAgentMenu = () => { agentMenuOpen.value = false; };
const agentSummary = computed(() => {
  const n = form.selectedAgentIds.length;
  if (n === 0) return 'Select agents…';
  if (n === 1) {
    const a = props.agents.find(x => x.id === form.selectedAgentIds[0]);
    return a ? a.name : '1 selected';
  }
  return `${n} selected`;
});

// ---- Dirty tracking -------------------------------------------------------
// Compare every field against the initial blank state. A truthy diff means
// the user typed/selected something — close should warn before discarding.
const isDirty = computed(() => {
  const init = initialState();
  if (form.platform !== init.platform) return true;
  if (form.type     !== init.type)     return true;
  if (form.token.trim()) return true;
  if (form.name.trim())  return true;
  if (form.selectedAgentIds.length) return true;
  if (form.fangateMode !== init.fangateMode) return true;
  if (form.newVault.email.trim() || form.newVault.token.trim()) return true;
  // Wizard progress beyond the initial risk step counts as dirty so a close
  // confirms before throwing away an entered code/password or a scanned QR.
  if (form.type === 'enduser' && wizard.phase !== 'idle' && wizard.phase !== 'risk') {
    return true;
  }
  return false;
});

// ---- Validation hints + submit ready --------------------------------------
const tokenLabel = computed(() => {
  if (form.platform !== 'telegram') return 'API token';
  if (form.type === 'personal') return 'Bot token (from @BotFather — same as Bot-Account)';
  return 'Bot token (from @BotFather)';
});
const tokenPlaceholder = computed(() => {
  if (form.platform !== 'telegram') return 'Paste the API token…';
  return 'e.g. 5234567890:AAEhBOweik6ad...';
});
// A typed new-vault Fangate token must pass "Test" (validates against /products)
// before the account can be saved — otherwise an invalid/untested token would be
// stored silently (audit #4). An empty token = "no Fangate link yet" → allowed.
const needsTokenTest = computed(() =>
  form.fangateMode === '__new__' && !!form.newVault.token.trim() && fangateValid.value !== true
);
// Linking a Fangate vault REQUIRES the creator's Fangate login email — it's what
// lets us track that account's sales (payment webhooks). Blocks Connect until set.
const needsFangateEmail = computed(() => {
  const linkingExisting = !!form.fangateMode && form.fangateMode !== '__new__';
  const linkingNew = form.fangateMode === '__new__' && !!form.newVault.token.trim();
  return (linkingExisting || linkingNew) && !form.newVault.email.trim();
});
const submitReady = computed(() => {
  if (form.name.trim().length === 0) return false;
  if (needsTokenTest.value) return false;
  if (needsFangateEmail.value) return false;
  // Enduser flow: the wizard creates the channel+inbox itself. Connect button
  // here just applies fangate + agents to that inbox; only enabled once the
  // wizard reaches its 'done' phase.
  if (form.type === 'enduser') return wizard.phase === 'done' && !!wizard.result?.inbox_id;
  // Bot / Personal: bot_token is required.
  return form.token.trim().length >= 8;
});

// ---- Fangate-token validation status pill (for the new-vault sub-form) ---
const fangateValidating = ref(false);
const fangateValid      = ref(null);
const fangateError      = ref('');
watch(() => form.newVault.token, () => {
  fangateValid.value = null;
  fangateError.value = '';
});
const testFangateToken = async () => {
  const t = form.newVault.token.trim();
  if (!t) return;
  fangateValidating.value = true;
  fangateError.value      = '';
  try {
    await FangateAPI.validateToken(t);
    fangateValid.value = true;
  } catch (e) {
    fangateValid.value = false;
    fangateError.value = e?.message || 'Token validation failed.';
  } finally {
    fangateValidating.value = false;
  }
};

// ---- Submit ----------------------------------------------------------------
const isSubmitting = ref(false);
const submitError  = ref('');

const submit = async () => {
  if (!submitReady.value || isSubmitting.value) return;
  isSubmitting.value = true;
  submitError.value  = '';

  try {
    // Bot-Account and Personal-Account both use the same Channel::Telegram +
    // bot_token path. Telegram routes Personal traffic as `business_message`
    // updates to the SAME bot, which Chatwoot's IncomingMessageService already
    // handles (transform_business_message! sets business_connection_id on the
    // Conversation.additional_attributes). The chat list can read that flag
    // at runtime to show a Personal/Business badge — no inbox-level marker
    // needed.
    //
    // The "type" choice in this modal only drives the success-toast wording
    // (telling the creator what extra wiring they need to do in Telegram).
    const tgType = form.platform === 'telegram' ? form.type : 'bot';

    // ENDUSER (MTProto): the wizard has already created the Channel::TelegramUser
    // + Inbox + InboxMember on the backend. We just need to surface its id so
    // the Fangate + agents steps below can apply to it, like the bot flow.
    let channel;
    if (tgType === 'enduser') {
      channel = { id: wizard.result.inbox_id };
    } else {
      const channelPayload = {
        type: 'telegram',
        bot_token: form.token.trim(),
        // Account kind — persisted server-side (channel_telegram.account_type) so
        // the Chat-Accounts Type column is correct across devices/admins/Hetzner.
        // 'bot' | 'personal' (Telegram Premium); enduser uses Channel::TelegramUser.
        account_type: tgType,
      };
      channel = await store.dispatch('inboxes/createChannel', {
        channel: channelPayload,
        // Friendly inbox name override.
        name: form.name.trim(),
      });
    }

    // Optional Fangate link — either reuse an existing inbox's token or
    // create a fresh vault entry. Stored under fangate_token_<inboxId> so
    // FangateAPI.setActiveInbox(channel.id) picks it up immediately.
    if (form.fangateMode && form.fangateMode !== '__new__') {
      // Reuse an existing inbox's vault — the SERVER copies its stored token
      // across (the raw token is never exposed to the browser). Email comes
      // from the (pre-filled, possibly edited) form so the new row shows the
      // right Fangate account identity in the overview.
      const sourceInboxId = Number(form.fangateMode);
      const email = form.newVault.email.trim();
      FangateAPI.linkExistingForInbox(channel.id, sourceInboxId, email);
    } else if (form.fangateMode === '__new__') {
      const tok   = form.newVault.token.trim();
      const email = form.newVault.email.trim();
      // A vault needs a token to be usable; the email (now REQUIRED when linking
      // — see needsFangateEmail) is stored alongside it: shown in the overview
      // and used to track this account's Fangate sales.
      if (tok) FangateAPI.setLinkForInbox(channel.id, { token: tok, email });
    }

    // Persist agent assignments. inboxes/addAgents action takes the inbox
    // id + array of user ids.
    if (form.selectedAgentIds.length) {
      try {
        await store.dispatch('inboxMembers/create', {
          inboxId: channel.id,
          agentList: form.selectedAgentIds,
        });
      } catch (_) {
        // Non-fatal — admin can re-assign from the inbox detail page.
      }
    }

    useAlert(
      tgType === 'personal'
        ? 'Account added. Now open Telegram → Settings → Telegram Business → Chatbots and add this bot\'s @username to route your personal DMs.'
        : tgType === 'enduser'
          ? 'Enduser account connected. Reply-only is active; your existing chats, groups and channels will appear in the sidebar.'
          : 'Account added.'
    );
    emit('created', channel);
    forceClose();
  } catch (e) {
    submitError.value = e?.message
      || 'Failed to create channel — verify the token format and try again.';
  } finally {
    isSubmitting.value = false;
  }
};

// ---- Close handlers --------------------------------------------------------
// Reset state on close so the next open starts blank.
const resetForm = () => {
  Object.assign(form, initialState());
  submitError.value = '';
  fangateValid.value = null;
  fangateError.value = '';
  agentMenuOpen.value = false;
  wizard.phase = 'idle';
};

const forceClose = () => {
  // If an MTProto wizard attempt is mid-flight, release the sidecar's auth
  // slot now (don't wait for the 10-min GC) so a freshly typed phone or code
  // is invalidated immediately. Fire-and-forget — close shouldn't block on it.
  if (form.type === 'enduser' && wizard.phase !== 'idle' && wizard.phase !== 'done') {
    cancelWizard().catch(() => {});
  }
  emit('update:open', false);
  // wait for the modal exit before resetting so we don't flash the form
  // back to defaults during the close transition.
  nextTick(() => setTimeout(resetForm, 50));
};

// Reconnect footer button. On 'done' the backend already re-authed the inbox in
// place → emit 'created' so the parent refreshes (health → healthy), then close.
// Before 'done', "Close" just cancels the in-flight auth (via forceClose).
const finishReconnect = () => {
  if (wizard.phase === 'done') emit('created');
  forceClose();
};

// Custom in-modal confirm so we stay in corporate design instead of the
// browser's native window.confirm popup. Opened by tryClose, dismissed by
// either button (Discard → forceClose, Cancel → just hide the inner overlay).
const discardConfirmOpen = ref(false);
const tryClose = () => {
  if (!isDirty.value) { forceClose(); return; }
  discardConfirmOpen.value = true;
};
const confirmDiscard = () => { discardConfirmOpen.value = false; forceClose(); };
const cancelDiscard  = () => { discardConfirmOpen.value = false; };
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fa-modal-overlay" @click.self.stop>
      <div class="fa-modal" @click.stop>
        <!-- ===================== Header ===================== -->
        <div class="fa-modal-head">
          <div class="fa-modal-headmeta">
            <h2 class="fa-modal-title">{{ isReconnect ? ('Reconnect ' + (reconnectInbox?.name || 'account')) : 'Add account' }}</h2>
            <p class="fa-modal-sub">Connect a new chat account</p>
          </div>
          <button class="fa-modal-close" type="button" @click="tryClose"
                  :title="isDirty ? 'Discard changes & close' : 'Close'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.4"
                 stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- ===================== Body ===================== -->
        <div class="fa-modal-body">

          <!-- 1. Platform -->
          <div v-if="!isReconnect" class="fa-step">
            <label class="fa-step-label">1. Platform</label>
            <div class="fa-pill-grid fa-pill-grid--2">
              <button v-for="p in PLATFORM_OPTIONS" :key="p.id"
                      type="button" class="fa-pill"
                      :class="{ selected: form.platform === p.id, disabled: p.disabled }"
                      :disabled="p.disabled"
                      @click="selectPlatform(p.id)">
                <!-- Telegram paper-plane mark (single-path, properly centered) -->
                <svg v-if="p.id === 'telegram'" class="fa-pill-brand" viewBox="0 0 240 240"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="120" cy="120" r="120" fill="#229ED9"/>
                  <path fill="#fff" d="M53.5 116.8c34.9-15.2 58.2-25.2 69.8-30 33.3-13.9 40.2-16.3 44.7-16.4 1 0 3.2.2 4.7 1.4.6.5 1 1.4 1.2 2.5.2 1.1.4 3.5.2 5.4-2.2 22.7-11.6 77.9-16.4 103.4-2 10.8-6 14.4-9.8 14.8-8.4.8-14.7-5.5-22.8-10.8-12.7-8.3-19.8-13.5-32.2-21.6-14.3-9.4-5-14.6 3.1-23 2.1-2.2 39.1-35.8 39.8-38.9.1-.4.2-1.8-.7-2.5-.9-.7-2.2-.5-3.1-.3-1.3.3-22.7 14.4-64 42.4-6 4.2-11.5 6.2-16.4 6.1-5.4-.1-15.8-3.1-23.5-5.6-9.5-3.1-17-4.7-16.4-9.9.3-2.7 4.1-5.5 11.4-8.5z"/>
                </svg>
                <!-- WhatsApp green bubble + white handset (separate paths) -->
                <svg v-else-if="p.id === 'whatsapp'" class="fa-pill-brand" viewBox="0 0 256 256"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#25D366" d="M128 0C57.3 0 0 57.3 0 128c0 22.5 5.8 43.7 16 62L0 256l67-15.8c17.6 9.6 37.7 15 59 15 70.7 0 128-57.3 128-128S198.7 0 128 0z"/>
                  <path fill="#fff" d="M188.3 154.3c-3.3-1.7-19.5-9.6-22.5-10.7-3-1.1-5.2-1.7-7.4 1.7-2.2 3.3-8.5 10.7-10.4 12.9-1.9 2.2-3.9 2.5-7.1.8-3.3-1.7-13.8-5.1-26.3-16.3-9.7-8.7-16.3-19.4-18.2-22.7-1.9-3.3-.2-5.1 1.4-6.8 1.5-1.5 3.3-3.9 5-5.8 1.7-1.9 2.2-3.3 3.3-5.5 1.1-2.2.5-4.2-.3-5.8-.8-1.7-7.4-17.8-10.1-24.4-2.6-6.4-5.4-5.5-7.4-5.6-1.9-.1-4.1-.1-6.4-.1-2.2 0-5.8.8-8.8 4.1-3 3.3-11.6 11.3-11.6 27.5s11.9 31.9 13.5 34.1c1.7 2.2 23.4 35.7 56.7 50.1 7.9 3.4 14.1 5.5 18.9 7 7.9 2.5 15.2 2.2 20.9 1.3 6.4-1 19.5-8 22.3-15.7 2.7-7.7 2.7-14.4 1.9-15.7-.8-1.3-3-2.1-6.4-3.7z"/>
                </svg>
                {{ p.label }}
                <span v-if="p.disabled" class="fa-pill-soon">soon</span>
              </button>
            </div>
          </div>

          <!-- 2. Type (Telegram only) -->
          <div v-if="!isReconnect && form.platform === 'telegram'" class="fa-step">
            <label class="fa-step-label">2. Type</label>
            <div class="fa-pill-grid fa-pill-grid--3">
              <button v-for="t in TG_TYPE_OPTIONS" :key="t.id"
                      type="button" class="fa-pill fa-pill--stacked"
                      :class="{ selected: form.type === t.id }"
                      @click="selectType(t.id)">
                <span class="fa-pill-label">{{ t.label }}</span>
                <span v-if="t.subtitle" class="fa-pill-subtitle">{{ t.subtitle }}</span>
              </button>
            </div>
            <div v-if="currentType" class="fa-hint">
              <p v-if="currentType.intro" class="fa-hint-intro">
                {{ currentType.intro }}
              </p>
              <ol v-if="currentType.steps?.length" class="fa-hint-steps">
                <li v-for="(s, i) in currentType.steps" :key="i">
                  <div class="fa-hint-step-title">{{ s.title }}</div>
                  <div class="fa-hint-step-body">{{ s.body }}</div>
                </li>
              </ol>
            </div>
          </div>

          <!-- 3. API token (Bot / Personal) -->
          <div v-if="form.type !== 'enduser'" class="fa-step">
            <label class="fa-step-label">3. {{ tokenLabel }}</label>
            <input v-model="form.token" type="text"
                   class="fa-input fa-input--mono"
                   :placeholder="tokenPlaceholder" />
          </div>

          <!-- 3. Enduser wizard (MTProto). Replaces the bot-token step:
               disclosure → login method → phone/SMS or QR → optional 2FA →
               backend already created the inbox by the time we hit 'done'. -->
          <div v-else class="fa-step fa-wizard">
            <label class="fa-step-label">3. Connect your Telegram account</label>

            <!-- Phase: RISK disclosure -->
            <div v-if="wizard.phase === 'risk'" class="fa-wiz-card">
              <div class="fa-wiz-title">⚠️ Important — your real Telegram account</div>
              <p class="fa-wiz-body">
                Connecting an Enduser Account links your <strong>personal
                Telegram identity</strong> to Creator-Chat. Ban risk hits your
                REAL account.
              </p>
              <p class="fa-wiz-body">
                <strong>What works:</strong> Your existing chats, groups and
                channels appear automatically — no per-group bot setup, no
                limitations like Telegram Premium.
              </p>
              <p class="fa-wiz-body">
                <strong>Limitation (always on).</strong>
                You can reply to anyone who messaged you first; you cannot
                start a conversation with someone who never wrote to you. This
                is Telegram's safest lane for user accounts.
                If you want to enable outbound to new contacts —
                <strong>please contact support</strong>.
              </p>
              <p class="fa-wiz-fine fa-wiz-fine--danger">
                <strong>How to avoid a ban:</strong> Use an aged, real-SIM
                number. Never a freshly registered one. No mass messaging, no
                spam.
              </p>
              <label class="fa-wiz-check">
                <input v-model="wizard.acceptedRisk" type="checkbox" />
                I understand the risks and the Reply-Only restriction.
              </label>
              <button type="button" class="fa-wiz-primary"
                      :disabled="!wizard.acceptedRisk"
                      @click="acceptRisk">
                Continue
              </button>
            </div>

            <!-- Phase: API CREDENTIALS gate (#35 own-api_id-first) -->
            <div v-else-if="wizard.phase === 'apicreds'" class="fa-wiz-card">
              <div class="fa-wiz-title">First, your own Telegram API keys</div>
              <p class="fa-wiz-body">
                Creator-Chat connects your accounts under <strong>your own
                Telegram API credentials</strong>. This keeps your agency isolated
                and your accounts safe. One-time setup, ~2 minutes.
              </p>
              <ol class="fa-wiz-ol">
                <li>Open <a href="https://my.telegram.org/auth" target="_blank" rel="noopener noreferrer">my.telegram.org</a>
                    and log in with your Telegram phone number (a code arrives in your Telegram app).</li>
                <li>Click <strong>API development tools</strong>.</li>
                <li>Fill the short form — App title &amp; Short name can be anything
                    (e.g. "CreatorChat"), platform <strong>Desktop</strong>, URL can stay empty.</li>
                <li>Click <strong>Create application</strong>.</li>
                <li>Copy the <strong>App api_id</strong> (a number) and the
                    <strong>App api_hash</strong> (a long string) into the fields below.</li>
              </ol>
              <p class="fa-wiz-fine fa-wiz-fine--danger">
                Keep these private — they identify your apps to Telegram. Never share them publicly.
              </p>
              <template v-if="!wizard.useDefaultCreds">
                <label class="fa-wiz-field-label">api_id</label>
                <input v-model="wizard.apiIdInput" type="text" class="fa-input fa-input--mono"
                       placeholder="1234567" inputmode="numeric" autocomplete="off" />
                <label class="fa-wiz-field-label">api_hash</label>
                <input v-model="wizard.apiHashInput" type="text" class="fa-input fa-input--mono"
                       placeholder="0123456789abcdef0123456789abcdef" autocomplete="off" />
              </template>
              <div v-else class="fa-wiz-default-note">
                Creator-Chat's default API credentials will be used.
              </div>
              <label v-if="wizard.defaultAvailable" class="fa-wiz-default-creds">
                <input type="checkbox" v-model="wizard.useDefaultCreds" @change="toggleDefaultCreds" />
                <span>(or use Creator-Chat default credentials)
                  <small>Using your own API credentials is the lower-risk choice — it keeps your agency isolated.</small>
                </span>
              </label>
              <div v-if="wizard.error" class="fa-wiz-error">{{ wizard.error }}</div>
              <div class="fa-wiz-row">
                <button v-if="!isReconnect" type="button" class="fa-wiz-ghost"
                        @click="wizard.phase = 'risk'">
                  Back
                </button>
                <button type="button" class="fa-wiz-primary"
                        :disabled="wizard.busy || (!wizard.useDefaultCreds && (!wizard.apiIdInput.trim() || !wizard.apiHashInput.trim()))"
                        @click="saveApiCreds">
                  {{ wizard.busy ? 'Verifying…' : 'Save & continue' }}
                </button>
              </div>
            </div>

            <!-- Phase: COUNTRY (#3) — drives the proxy region -->
            <div v-else-if="wizard.phase === 'country'" class="fa-wiz-card">
              <div class="fa-wiz-title">Where is this account registered?</div>
              <p class="fa-wiz-fine">
                Pick the country this Telegram number belongs to. It sets the proxy
                region the account's traffic runs through — so the account stays
                geographically consistent with its number.
              </p>
              <label class="fa-wiz-field-label">Country</label>
              <select v-model="wizard.country" class="fa-input">
                <option value="">Select a country…</option>
                <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.name }}</option>
              </select>
              <p v-if="wizard.country" class="fa-wiz-fine">
                Proxy region: <strong>{{ proxyRegionLabel(wizard.country) }}</strong>
              </p>
              <div v-if="wizard.error" class="fa-wiz-error">{{ wizard.error }}</div>
              <div class="fa-wiz-row">
                <button type="button" class="fa-wiz-ghost" @click="wizard.phase = 'risk'">
                  Back
                </button>
                <button type="button" class="fa-wiz-primary"
                        :disabled="!wizard.country"
                        @click="confirmCountry">
                  Continue
                </button>
              </div>
              <button v-if="wizard.credsPresent" type="button" class="fa-wiz-relink" @click="reenterApiCreds">
                Wrong API keys? Change them
              </button>
            </div>

            <!-- Phase: METHOD picker -->
            <div v-else-if="wizard.phase === 'method'" class="fa-wiz-card">
              <div class="fa-wiz-title">Choose how to sign in</div>
              <label class="fa-wiz-radio">
                <input v-model="wizard.loginMethod" type="radio" value="qr" />
                <span><strong>QR code <em class="fa-wiz-badge">easiest &amp; safest</em></strong>
                  <small>Scan with Telegram → Settings → Devices → Link Desktop Device. No code to type.</small>
                </span>
              </label>
              <label class="fa-wiz-radio">
                <input v-model="wizard.loginMethod" type="radio" value="device" />
                <span><strong>Phone &amp; login code</strong>
                  <small>Sign in right here in your browser — the code goes to your Telegram app.</small>
                </span>
              </label>
              <div class="fa-wiz-row">
                <button type="button" class="fa-wiz-ghost" @click="wizard.phase = 'risk'">
                  Back
                </button>
                <button type="button" class="fa-wiz-primary" @click="startMethod">
                  Continue
                </button>
              </div>
              <button v-if="wizard.credsPresent" type="button" class="fa-wiz-relink" @click="reenterApiCreds">
                Wrong API keys? Change them
              </button>
            </div>

            <!-- Phase: PHONE number -->
            <div v-else-if="wizard.phase === 'phone'" class="fa-wiz-card">
              <div class="fa-wiz-title">Your Telegram phone number</div>
              <p class="fa-wiz-fine">International format, e.g. <code>+49170…</code></p>
              <p class="fa-wiz-fine">
                The login runs <strong>right here in your browser</strong>, so the
                code is requested from your own connection. Creator-Chat never sees
                your phone number or password.
              </p>
              <input v-model="wizard.phone" type="tel" class="fa-input"
                     placeholder="+49…" autocomplete="off"
                     @keydown.enter.prevent="deviceStart" />
              <div v-if="wizard.error" class="fa-wiz-error">{{ wizard.error }}</div>
              <div class="fa-wiz-row">
                <button type="button" class="fa-wiz-ghost" @click="wizard.phase = 'method'">
                  Back
                </button>
                <button type="button" class="fa-wiz-primary"
                        :disabled="wizard.busy || !wizard.phone.trim()"
                        @click="deviceStart">
                  {{ wizard.busy ? 'Connecting…' : 'Send code' }}
                </button>
              </div>
            </div>

            <!-- Phase: PHONE code -->
            <div v-else-if="wizard.phase === 'phone_code'" class="fa-wiz-card">
              <div class="fa-wiz-title">Enter the login code</div>
              <p class="fa-wiz-fine">
                Telegram sent a code <strong>inside your Telegram app</strong>
                (not via SMS) — look for a message from "Telegram".
              </p>
              <input v-model="wizard.code" type="text" class="fa-input fa-input--mono"
                     placeholder="12345" inputmode="numeric" autocomplete="one-time-code"
                     @keydown.enter.prevent="deviceSubmitCode" />
              <div v-if="wizard.error" class="fa-wiz-error">{{ wizard.error }}</div>
              <div class="fa-wiz-row">
                <button type="button" class="fa-wiz-ghost"
                        @click="cancelWizard().then(resetWizard)">
                  Restart
                </button>
                <button type="button" class="fa-wiz-primary"
                        :disabled="wizard.busy || !wizard.code.trim()"
                        @click="deviceSubmitCode">
                  {{ wizard.busy ? 'Verifying…' : 'Verify' }}
                </button>
              </div>
            </div>

            <!-- Phase: 2FA password (shared by phone + QR) -->
            <div v-else-if="wizard.phase === 'password'" class="fa-wiz-card">
              <div class="fa-wiz-title">Two-factor password</div>
              <p class="fa-wiz-fine">
                This account has two-factor authentication enabled.
                Enter the password you set in Telegram.
              </p>
              <input v-model="wizard.password" type="password" class="fa-input"
                     autocomplete="current-password"
                     @keydown.enter.prevent="deviceSubmitPassword" />
              <div v-if="wizard.error" class="fa-wiz-error">{{ wizard.error }}</div>
              <div class="fa-wiz-row">
                <button type="button" class="fa-wiz-ghost"
                        @click="cancelWizard().then(resetWizard)">
                  Restart
                </button>
                <button type="button" class="fa-wiz-primary"
                        :disabled="wizard.busy || !wizard.password"
                        @click="deviceSubmitPassword">
                  {{ wizard.busy ? 'Verifying…' : 'Verify' }}
                </button>
              </div>
            </div>

            <!-- Phase: QR code -->
            <div v-else-if="wizard.phase === 'qr'" class="fa-wiz-card">
              <div class="fa-wiz-title">Scan with your Telegram app</div>
              <p class="fa-wiz-fine">
                Open <strong>Telegram → Settings → Devices → Link Desktop
                Device</strong> on your phone and scan this code.
              </p>
              <div class="fa-wiz-qr-wrap">
                <img v-if="wizard.qrDataUrl" :src="wizard.qrDataUrl" alt="Telegram login QR" />
                <div v-else class="fa-wiz-qr-loading">Generating QR…</div>
              </div>
              <p v-if="wizard.busy" class="fa-wiz-fine">Waiting for scan…</p>
              <div v-if="wizard.error" class="fa-wiz-error">{{ wizard.error }}</div>
              <div class="fa-wiz-row">
                <button type="button" class="fa-wiz-ghost"
                        @click="cancelWizard().then(resetWizard)">
                  Restart
                </button>
              </div>
            </div>

            <!-- Phase: DONE -->
            <div v-else-if="wizard.phase === 'done'" class="fa-wiz-card fa-wiz-card--ok">
              <div class="fa-wiz-title">✅ {{ isReconnect ? 'Reconnected' : 'Connected' }}</div>
              <p class="fa-wiz-body">
                Signed in as
                <strong>{{ wizard.result?.me?.first_name || 'your account' }}</strong>
                <span v-if="wizard.result?.me?.username">
                  (@{{ wizard.result.me.username }})</span>.
                <template v-if="isReconnect">
                  The account is back online — your chats and history are intact.
                </template>
                <template v-else>
                  Reply-Only is active. Pick a name, agents and an optional
                  Fangate link below, then hit <strong>Connect</strong>.
                </template>
              </p>
            </div>
          </div>

          <!-- 4. Account name -->
          <div v-if="!isReconnect" class="fa-step">
            <label class="fa-step-label">4. Account name</label>
            <input v-model="form.name" type="text" class="fa-input"
                   placeholder="e.g. Leonie" maxlength="40" />
          </div>

          <!-- 5. Agents (click-to-expand multi-select dropdown) -->
          <div v-if="!isReconnect && agents.length" class="fa-step">
            <label class="fa-step-label">5. Assign agents</label>
            <div class="fa-agent-dropdown" v-on-click-outside="closeAgentMenu">
              <button type="button" class="fa-agent-trigger"
                      :class="{ open: agentMenuOpen }"
                      @click="agentMenuOpen = !agentMenuOpen">
                <span class="fa-agent-trigger-label">{{ agentSummary }}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.5"
                     stroke-linecap="round" stroke-linejoin="round"
                     :class="{ 'fa-rot-180': agentMenuOpen }">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <div v-if="agentMenuOpen" class="fa-agent-menu">
                <button v-for="a in assignableAgents" :key="a.id" type="button"
                        class="fa-agent-item"
                        :class="{ selected: form.selectedAgentIds.includes(a.id) }"
                        @click="toggleAgent(a.id)">
                  <span class="fa-agent-check-box">
                    <svg v-if="form.selectedAgentIds.includes(a.id)"
                         width="10" height="10" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="3.5"
                         stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                  {{ a.name }}
                </button>
                <div v-if="!assignableAgents.length"
                     style="padding:8px 12px;font-family:'Inter',sans-serif;font-size:11.5px;color:#6e7570;">
                  No agents yet — admins always have access to every account.
                </div>
              </div>
            </div>
          </div>

          <!-- 6. Fangate link -->
          <div v-if="!isReconnect" class="fa-step">
            <label class="fa-step-label">6. Fangate link</label>
            <select v-model="form.fangateMode" class="fa-input">
              <option value="">— Don't link —</option>
              <option v-for="v in fangateVaults" :key="v.id" :value="String(v.id)">
                ⚡ {{ v.name }}{{ v.email ? ' — ' + v.email : '' }}
              </option>
              <option value="__new__">+ Add new vault</option>
            </select>

            <!-- The Live-Sales webhook URL is per chat-account, so it can only be
                 issued AFTER the account exists — nudge the admin to grab it in Edit. -->
            <p v-if="form.fangateMode" class="fa-fineprint" style="color:#b91c1c;">
              ⚠️ <strong>IMPORTANT</strong> — After creating, open <strong>Edit account</strong> to get this account's
              webhook URL and register it at <strong>app.fangate.info → Settings → API</strong> — required
              for <strong>live sales tracking</strong>, notifications and reports.
            </p>

            <!-- Existing vault picked: email is pre-filled from that vault and
                 stays editable; the vault's API token is reused as-is. -->
            <div v-if="form.fangateMode && form.fangateMode !== '__new__'" class="fa-new-vault">
              <label class="fa-sub-label">Fangate email <span class="fa-req">*</span></label>
              <input v-model="form.newVault.email" type="email"
                     class="fa-input" placeholder="e.g. leonie@fangate.app" />
              <p class="fa-fineprint">
                Use the email you log in to Fangate with — this lets you track
                this account's sales and get live and in-chat notifications of
                successful sales.
              </p>
            </div>

            <div v-if="form.fangateMode === '__new__'" class="fa-new-vault">
              <div class="fa-new-vault-head">
                <strong>NEW FANGATE VAULT</strong>
              </div>

              <label class="fa-sub-label">Fangate email <span class="fa-req">*</span></label>
              <input v-model="form.newVault.email" type="email"
                     class="fa-input" placeholder="e.g. leonie@fangate.app" />
              <p class="fa-fineprint">
                Use the email you log in to Fangate with — this lets you track
                this account's sales and get live and in-chat notifications of
                successful sales.
              </p>

              <label class="fa-sub-label">
                Fangate API token
                <span v-if="fangateValid === true" class="fa-tag fa-tag--ok">✓ Verified</span>
                <span v-else-if="fangateValid === false" class="fa-tag fa-tag--err">✗ Invalid</span>
              </label>
              <div class="fa-token-row">
                <input v-model="form.newVault.token" type="text"
                       class="fa-input fa-input--mono"
                       placeholder="fg_live_…" />
                <button type="button" class="fa-test-btn"
                        :disabled="!form.newVault.token.trim() || fangateValidating"
                        @click="testFangateToken">
                  {{ fangateValidating ? 'Testing…' : 'Test' }}
                </button>
              </div>
              <p v-if="fangateError" class="fa-error-line">{{ fangateError }}</p>
              <p v-else-if="needsTokenTest" class="fa-fineprint" style="color:var(--accent-strong);">
                Click "Test" to verify this token before connecting.
              </p>

              <p class="fa-fineprint">
                Create the token in the Fangate dashboard under Settings → API Token.
              </p>
            </div>
          </div>

          <!-- Submit error -->
          <p v-if="submitError" class="fa-error-line">{{ submitError }}</p>
        </div>

        <!-- ===================== Footer ===================== -->
        <div class="fa-modal-foot">
          <!-- Reconnect mode: the backend re-auths the inbox on 'done' — no
               add-flow finalize. Just a Done/Close button. -->
          <button v-if="isReconnect" type="button" class="fa-btn-primary"
                  @click="finishReconnect">
            {{ wizard.phase === 'done' ? 'Done' : 'Close' }}
          </button>
          <button v-else type="button" class="fa-btn-primary"
                  :disabled="!submitReady || isSubmitting"
                  @click="submit">
            {{ isSubmitting ? 'Connecting…' : 'Connect' }}
          </button>
        </div>

        <!-- Discard-confirm overlay — corporate-design replacement for the
             browser's native window.confirm popup. Sits inside .fa-modal so it
             only dims the Add-account dialog and inherits its z-index. -->
        <div v-if="discardConfirmOpen" class="fa-confirm-veil" @click.self="cancelDiscard">
          <div class="fa-confirm" role="dialog" aria-modal="true">
            <div class="fa-confirm-title">Discard changes?</div>
            <p class="fa-confirm-body">
              You have unsaved changes. If you close now, everything you typed
              will be lost.
            </p>
            <div class="fa-confirm-row">
              <button type="button" class="fa-wiz-ghost" @click="cancelDiscard">
                Keep editing
              </button>
              <button type="button" class="fa-wiz-primary fa-wiz-primary--danger"
                      @click="confirmDiscard">
                Discard &amp; close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.fa-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(11, 20, 20, 0.45);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
  padding: 20px;
}
.fa-modal {
  background: linear-gradient(180deg, #faf8ff 0%, #fff 100%);
  border-radius: 16px;
  width: 100%; max-width: 560px;
  max-height: 92vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(20, 10, 60, 0.30);
  overflow: hidden;
  // Stacking context for the in-modal discard-confirm overlay (inset:0).
  position: relative;
}

// Header -------------------------------------------------------------------
.fa-modal-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 18px 22px; gap: 12px;
  border-bottom: 1px solid rgba(91, 46, 207, 0.12);
  background: #fff;
}
.fa-modal-headmeta { min-width: 0; }
.fa-modal-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px; font-weight: 800;
  color: #1a0a6b;
  margin: 0;
}
.fa-modal-sub {
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: #7c6fcd;
  margin: 2px 0 0;
}
.fa-modal-close {
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  color: #5b2ecf;
  cursor: pointer; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover { background: #5b2ecf; color: #fff; border-color: #5b2ecf; }
}

// Body ---------------------------------------------------------------------
.fa-modal-body {
  flex: 1; overflow-y: auto;
  padding: 18px 22px;
  display: flex; flex-direction: column; gap: 16px;
}
.fa-step { display: flex; flex-direction: column; gap: 8px; }
.fa-step-label {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #5b2ecf;
  display: inline-flex; align-items: center; gap: 8px;
}
.fa-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #4b3a8a; line-height: 1.5;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(91, 46, 207, 0.06);
  display: flex; flex-direction: column; gap: 8px;
}
.fa-hint-intro {
  margin: 0;
  color: #1a0a6b;
  font-weight: 500;
}
.fa-hint-steps {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: fa-step;
  display: flex; flex-direction: column; gap: 6px;
}
.fa-hint-steps > li {
  counter-increment: fa-step;
  position: relative;
  padding: 6px 8px 6px 30px;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  &::before {
    content: counter(fa-step);
    position: absolute;
    left: 6px; top: 6px;
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 50%;
    background: #5b2ecf;
    color: #fff;
    font-size: 10px; font-weight: 700;
  }
}
.fa-hint-step-title {
  font-weight: 700;
  color: #1a0a6b;
  font-size: 11.5px;
  margin-bottom: 2px;
}
.fa-hint-step-body {
  color: #4b3a8a;
  font-size: 11px;
}
.fa-warn {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #92400e; line-height: 1.5;
  padding: 8px 12px;
  border-radius: 7px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.25);
}

// Pills (Platform + Type) --------------------------------------------------
.fa-pill-grid {
  display: grid; gap: 8px;
  &--2 { grid-template-columns: 1fr 1fr; }
  &--3 { grid-template-columns: 1fr 1fr 1fr; }
}
.fa-pill {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 9px 12px;
  border-radius: 9px;
  border: 1.5px solid rgba(91, 46, 207, 0.15);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600;
  color: #1a0a6b;
  cursor: pointer;
  transition: all 0.12s;
  position: relative;
  &:hover:not(.disabled) { border-color: #7040e0; }
  &.selected {
    border-color: #5b2ecf;
    background: rgba(91, 46, 207, 0.08);
    box-shadow: 0 0 0 3px rgba(91, 46, 207, 0.12);
  }
  &.disabled { opacity: 0.5; cursor: not-allowed; }
}
.fa-pill-brand {
  width: 20px; height: 20px;
  flex-shrink: 0;
  display: block;
  vertical-align: middle;
}
.fa-pill-soon {
  font-size: 9px; font-weight: 700;
  padding: 2px 6px; border-radius: 999px;
  background: rgba(0, 0, 0, 0.06); color: var(--ink-muted, #6b7280);
  text-transform: uppercase; letter-spacing: 0.04em;
  margin-left: 4px;
}

// Stacked-text type pill (Bot-Account / Personal-Account) — label on top,
// subtitle below in muted gold for the Premium hint.
.fa-pill--stacked {
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  text-align: center;
}
.fa-pill-label {
  font-size: 13px;
  font-weight: 700;
  color: #1a0a6b;
}
.fa-pill-subtitle {
  font-size: 10px;
  font-weight: 600;
  color: #b45309;
  letter-spacing: 0.02em;
}

// Enduser (MTProto) onboarding wizard --------------------------------------
.fa-wizard { margin-top: 4px; }
.fa-wiz-card {
  display: flex; flex-direction: column; gap: 12px;
  padding: 14px;
  border-radius: 10px;
  background: rgba(91, 46, 207, 0.04);
  border: 1.5px solid rgba(91, 46, 207, 0.16);
  &--ok {
    background: rgba(34, 197, 94, 0.06);
    border-color: rgba(34, 197, 94, 0.35);
  }
}
.fa-wiz-title { font-size: 14px; font-weight: 800; color: #1a0a6b; }
.fa-wiz-body  { font-size: 13px; color: #1a0a6b; line-height: 1.5; margin: 0; }
.fa-wiz-fine  {
  font-size: 12px; color: #4b5563; line-height: 1.45; margin: 0;
  // Red treatment for the "how to avoid a ban" line — same font/size as the
  // other fineprint, just a danger color so it stands out from the neutral
  // ✅ / 🛡 pills above it.
  &--danger { color: #b91c1c; }
}
// Neutral info pill — base color, no traffic-light backgrounds. Light gray box
// so the pill is still visually distinguishable from the body text without
// pulling attention via color.
.fa-wiz-pill {
  font-size: 12px; line-height: 1.45;
  padding: 9px 11px; border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #1a0a6b;
}
.fa-wiz-check {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 12px; color: #1a0a6b;
  cursor: pointer;
  input[type="checkbox"] { margin-top: 2px; }
}
.fa-wiz-radio {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 9px 11px;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.15);
  background: #fff;
  cursor: pointer;
  font-size: 13px; color: #1a0a6b;
  &:has(input:checked) {
    border-color: #5b2ecf;
    box-shadow: 0 0 0 3px rgba(91, 46, 207, 0.10);
  }
  small { display: block; font-size: 11px; font-weight: 500; color: #4b5563; margin-top: 2px; }
}
// "easiest & safest" badge on the recommended (QR) login method (#35).
.fa-wiz-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(91, 46, 207, 0.10);
  color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700; font-style: normal;
  letter-spacing: 0.02em; text-transform: uppercase;
  vertical-align: middle;
}
// Numbered my.telegram.org setup steps in the api-credentials gate (#35).
.fa-wiz-ol {
  margin: 2px 0 0; padding-left: 20px;
  font-size: 12px; color: #4b5563; line-height: 1.5;
  li { margin-bottom: 5px; }
  a { color: #5b2ecf; font-weight: 600; }
}
.fa-wiz-field-label {
  display: block;
  font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700;
  color: #1a0a6b; margin: 2px 0 -2px;
}
/* Shown instead of the api_id/api_hash fields when "use default" is ticked (#35). */
.fa-wiz-default-note {
  font-family: 'Inter', sans-serif; font-size: 12.5px; color: #1a0a6b;
  background: rgba(91, 46, 207, 0.06); border: 1px solid rgba(91, 46, 207, 0.15);
  border-radius: 8px; padding: 10px 12px;
}
.fa-wiz-default-creds {
  display: flex; align-items: flex-start; gap: 8px; margin-top: 10px; cursor: pointer;
  input[type="checkbox"] { margin-top: 2px; flex: 0 0 auto; cursor: pointer; }
  span  { font-family: 'Inter', sans-serif; font-size: 12.5px; font-weight: 600; color: #1a0a6b; }
  small { display: block; font-weight: 400; font-size: 11.5px; color: #6b7280; margin-top: 1px; }
}
.fa-wiz-row { display: flex; justify-content: flex-end; gap: 8px; }
.fa-wiz-primary {
  padding: 8px 14px; border-radius: 8px;
  border: none; background: #5b2ecf; color: #fff;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #4c25b4; }
  // Danger variant used by the discard-confirm overlay.
  &--danger {
    background: #b91c1c;
    &:hover:not(:disabled) { background: #991b1b; }
  }
}

// Corporate-design replacement for the browser's native window.confirm popup.
// Lives INSIDE the Add-account modal so it only dims this dialog.
.fa-confirm-veil {
  position: absolute; inset: 0;
  background: rgba(20, 10, 60, 0.45);
  backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  z-index: 5;
}
.fa-confirm {
  background: linear-gradient(180deg, #faf8ff 0%, #fff 100%);
  border-radius: 12px;
  width: 100%; max-width: 380px;
  padding: 18px 18px 16px;
  box-shadow: 0 18px 40px rgba(20, 10, 60, 0.30);
  display: flex; flex-direction: column; gap: 10px;
}
.fa-confirm-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 16px; font-weight: 700;
  color: #1a0a6b;
}
.fa-confirm-body {
  font-size: 13px; color: #1a0a6b; line-height: 1.45; margin: 0;
}
.fa-confirm-row {
  display: flex; justify-content: flex-end; gap: 8px;
  margin-top: 4px;
}
.fa-wiz-ghost {
  padding: 8px 14px; border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff; color: #5b2ecf;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
  cursor: pointer;
  &:hover { border-color: #5b2ecf; }
}
/* Subtle text link to jump back and replace stored api credentials (#35). */
.fa-wiz-relink {
  display: block; margin: 10px auto 0; padding: 4px 6px;
  background: none; border: none; cursor: pointer;
  font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600;
  color: #6b7280; text-decoration: underline;
  &:hover { color: #5b2ecf; }
}
.fa-wiz-error {
  font-size: 12px; color: #b91c1c;
  background: rgba(185, 28, 28, 0.08);
  border: 1px solid rgba(185, 28, 28, 0.25);
  border-radius: 6px;
  padding: 8px 10px;
}
.fa-wiz-qr-wrap {
  display: flex; justify-content: center;
  padding: 14px; background: #fff; border-radius: 8px;
  img { width: 240px; height: 240px; display: block; }
}
.fa-wiz-qr-loading {
  width: 240px; height: 240px;
  display: flex; align-items: center; justify-content: center;
  color: #6b7280; font-size: 12px;
}

// Inputs -------------------------------------------------------------------
.fa-input {
  width: 100%; box-sizing: border-box;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b;
  outline: none;
  &:focus { border-color: #7040e0; box-shadow: 0 0 0 3px rgba(112, 64, 224, 0.12); }
  &--mono { font-family: 'JetBrains Mono', 'Inter', monospace; font-size: 12px; }
}

// Agents — click-to-expand multi-select dropdown -----------------------------
.fa-agent-dropdown {
  position: relative;
}
.fa-agent-trigger {
  width: 100%; box-sizing: border-box;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b; font-weight: 500;
  cursor: pointer;
  text-align: left;
  &:hover { border-color: #7040e0; }
  &.open { border-color: #5b2ecf; box-shadow: 0 0 0 3px rgba(91, 46, 207, 0.12); }
  svg { color: #7c6fcd; transition: transform 0.15s; }
}
.fa-rot-180 { transform: rotate(180deg); }
.fa-agent-trigger-label {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fa-agent-menu {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  z-index: 10;
  max-height: 220px; overflow-y: auto;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.22);
  background: #fff;
  box-shadow: 0 8px 24px rgba(20, 10, 60, 0.12);
  padding: 4px;
  display: flex; flex-direction: column; gap: 2px;
}
.fa-agent-item {
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 7px 9px;
  border-radius: 6px;
  border: none;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b; font-weight: 500;
  cursor: pointer;
  text-align: left;
  &:hover { background: rgba(91, 46, 207, 0.06); }
  &.selected { background: rgba(91, 46, 207, 0.10); color: #1a0a6b; }
}
.fa-agent-check-box {
  width: 16px; height: 16px;
  border-radius: 4px;
  border: 1.5px solid rgba(91, 46, 207, 0.30);
  background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  color: #5b2ecf;
  flex-shrink: 0;
  .fa-agent-item.selected & {
    background: #5b2ecf;
    border-color: #5b2ecf;
    color: #fff;
  }
}

// New-vault sub-form -------------------------------------------------------
.fa-new-vault {
  padding: 12px 14px;
  border-radius: 9px;
  background: rgba(91, 46, 207, 0.05);
  border: 1px solid rgba(91, 46, 207, 0.20);
  display: flex; flex-direction: column; gap: 8px;
}
.fa-new-vault-head {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.12em; color: #5b2ecf;
}
.fa-sub-label {
  display: flex; align-items: center; gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  color: #5b2ecf;
}
/* Required-field asterisk */
.fa-req {
  color: #e5484d;
  font-weight: 800;
  margin-left: -4px;
}
.fa-token-row { display: flex; gap: 6px; }
.fa-test-btn {
  padding: 9px 14px;
  border-radius: 7px;
  border: 1.5px solid rgba(91, 46, 207, 0.3);
  background: #fff;
  color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: linear-gradient(135deg, #5b2ecf, #7040e0); color: #fff; border-color: transparent; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.fa-tag {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  padding: 1px 7px; border-radius: 999px;
  &--ok  { background: rgba(16, 185, 129, 0.16); color: #047857; }
  &--err { background: rgba(185, 28, 28, 0.14);  color: #b91c1c; }
}
.fa-fineprint {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: #7c6fcd; line-height: 1.4;
  margin: 0;
}
.fa-error-line {
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: #b91c1c;
  margin: 0;
}

// Footer -------------------------------------------------------------------
.fa-modal-foot {
  padding: 14px 22px;
  border-top: 1px solid rgba(91, 46, 207, 0.12);
  background: rgba(91, 46, 207, 0.03);
  display: flex; justify-content: flex-end;
}
.fa-btn-primary {
  padding: 9px 22px;
  border-radius: 9px;
  border: none;
  background: linear-gradient(135deg, #5b2ecf, #7040e0);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(91, 46, 207, 0.3);
  &:hover:not(:disabled) { background: linear-gradient(135deg, #4a1eaa, #5b2ecf); }
  &:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
}
</style>
